import type {SvgIconComponent} from "@mui/icons-material";
import type {AuthenticatedUser} from "auth/types";
import type {ReactNode} from "react";
import routes from "router/routes";
import type {Page, PageGroup} from "router/types";
import {isRoleAtLeast} from "utils/roles";

export type NavPage = {
  title: string;
  segment: string;
  icon: ReactNode;
};

export type NavPageGroup = {
  title: string;
  segment: string;
  icon: ReactNode;
  children: Array<NavPage | NavPageGroup>;
};

export type NavItem = NavPage | NavPageGroup;

export const isPage = (node: Page | PageGroup): node is Page =>
  (node as Page).Component !== undefined;

export const isPageGroupWithChildren = (
  node: Page | PageGroup,
): node is PageGroup & {children: Array<Page | PageGroup>} =>
  "children" in node &&
  Array.isArray((node as PageGroup).children) &&
  (node as PageGroup).children.length > 0;

const iconToElement = (IconOrElement: SvgIconComponent): ReactNode => {
  if (
    typeof IconOrElement === "function" ||
    typeof IconOrElement === "object"
  ) {
    return <IconOrElement />;
  }
};

/**
 * Accepts a list of pages/groups and filters them based on the user's role.
 * @param items - List of `Page` or `PageGroup` items to filter.
 * @param user - The authenticated user object.
 * @returns Filtered list of navigation items.
 */
export const filterPages = (
  items: Array<Page | PageGroup>,
  user: AuthenticatedUser,
): Array<NavItem> => {
  return items.flatMap(item => {
    if ((item as Partial<Page | PageGroup>).hideFromNav) return [];

    if (item.canAccess && !item.canAccess(user)) return [];

    if (isPageGroupWithChildren(item)) {
      if (
        item.roles &&
        !item.roles.some(role => isRoleAtLeast(user.role, role))
      ) {
        return [];
      }

      const filteredChildren = filterPages(item.children, user);
      if (filteredChildren.length === 0) return [];

      if (filteredChildren.length === 1) {
        const child = filteredChildren[0];
        return [
          {
            ...child,
            title: child.title,
            segment: `${item.segment}/${child.segment}`,
          },
        ];
      }

      return [
        {
          title: item.title,
          segment: item.segment,
          icon: iconToElement(item.Icon),
          children: filteredChildren,
        } as NavPageGroup,
      ];
    }

    if (isPage(item)) {
      if (
        item.roles &&
        !item.roles.some(role => isRoleAtLeast(user.role, role))
      ) {
        return [];
      }

      return [
        {
          title: item.title,
          segment: item.segment,
          icon: iconToElement(item.Icon),
        } as NavPage,
      ];
    }

    return [];
  });
};

export const getPageTitle = (pathSegment: string): string | null => {
  const findTitle = (
    items: Array<Page | PageGroup>,
    remainingPath: string[],
  ): string | null => {
    const [current, ...rest] = remainingPath;

    for (const item of items) {
      if (item.segment !== current) continue;

      if (rest.length === 0) {
        if (isPage(item)) return item.title;

        if (isPageGroupWithChildren(item)) {
          const leaf = item.children.find(isPage);
          return leaf?.title ?? item.title;
        }
      }

      if (isPageGroupWithChildren(item)) {
        return findTitle(item.children, rest);
      }

      return null;
    }

    return null;
  };

  return findTitle(routes, pathSegment.split("/"));
};

export const getNavigation = (
  user: AuthenticatedUser,
  routesToUse?: Array<Page | PageGroup>,
): Array<NavItem> => {
  const items = routesToUse ?? routes;
  return filterPages(items, user);
};

export default getNavigation;
